<template>
  <div class="admin-books-container">
    <el-container>
      <el-header>
        <div class="header-content">
          <div class="header-left">
            <el-button type="text" @click="goBack">
              <el-icon><arrow-left /></el-icon>
              返回
            </el-button>
            <h1>书籍管理</h1>
          </div>
          <div class="header-right">
            <el-button type="primary" @click="showUploadDialog = true">
              <el-icon><upload /></el-icon>
              上传书籍
            </el-button>
          </div>
        </div>
      </el-header>

      <el-main>
        <!-- 搜索和筛选 -->
        <div class="filter-bar">
          <el-input
            v-model="searchKeyword"
            placeholder="搜索书名或作者"
            prefix-icon="search"
            style="width: 300px"
            @keyup.enter="fetchBooks"
          />
          <el-button type="primary" @click="fetchBooks">
            搜索
          </el-button>
          <el-button @click="resetFilter">
            重置
          </el-button>
        </div>

        <!-- 书籍表格 -->
        <el-table
          v-loading="loading"
          :data="books"
          style="width: 100%"
          border
        >
          <el-table-column prop="id" label="ID" width="80" />
          <el-table-column prop="title" label="书名" />
          <el-table-column prop="author" label="作者" width="120" />
          <el-table-column prop="file_size" label="文件大小" width="120">
            <template #default="scope">
              {{ formatFileSize(scope.row.file_size) }}
            </template>
          </el-table-column>
          <el-table-column prop="total_chars" label="总字数" width="120">
            <template #default="scope">
              {{ scope.row.total_chars || 0 }}
            </template>
          </el-table-column>
          <el-table-column prop="created_at" label="上传时间" width="180">
            <template #default="scope">
              {{ formatDate(scope.row.created_at) }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="200" fixed="right">
            <template #default="scope">
              <el-button
                type="text"
                size="small"
                @click="editBook(scope.row)"
              >
                编辑
              </el-button>
              <el-button
                type="text"
                size="small"
                @click="previewBook(scope.row.id)"
              >
                预览
              </el-button>
              <el-button
                type="text"
                size="small"
                @click="deleteBook(scope.row.id)"
                style="color: #F56C6C"
              >
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <el-pagination
          v-if="total > 0"
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />

        <!-- 空状态 -->
        <el-empty v-if="books.length === 0 && !loading" description="暂无书籍" />
      </el-main>
    </el-container>

    <!-- 上传书籍对话框 -->
    <el-dialog v-model="showUploadDialog" title="上传书籍" width="500px">
      <el-form ref="uploadForm" :model="uploadForm" :rules="uploadRules" label-width="100px">
        <el-form-item label="书名" prop="title">
          <el-input v-model="uploadForm.title" placeholder="请输入书名" />
        </el-form-item>
        <el-form-item label="作者" prop="author">
          <el-input v-model="uploadForm.author" placeholder="请输入作者" />
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input
            v-model="uploadForm.description"
            type="textarea"
            :rows="3"
            placeholder="请输入书籍描述"
          />
        </el-form-item>
        <el-form-item label="文件" prop="file">
          <el-upload
            class="upload-demo"
            drag
            action="#"
            :auto-upload="false"
            :on-change="handleFileChange"
            :before-upload="() => false"
            accept=".txt"
          >
            <el-icon class="el-icon--upload"><upload-filled /></el-icon>
            <div class="el-upload__text">
              将TXT文件拖到此处，或<em>点击上传</em>
            </div>
            <template #tip>
              <div class="el-upload__tip">
                只能上传TXT文件，且不超过10MB
              </div>
            </template>
          </el-upload>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showUploadDialog = false">取消</el-button>
          <el-button type="primary" :loading="uploading" @click="handleUpload">
            上传
          </el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 编辑书籍对话框 -->
    <el-dialog v-model="showEditDialog" title="编辑书籍" width="500px">
      <el-form ref="editForm" :model="editForm" :rules="editRules" label-width="100px">
        <el-form-item label="书名" prop="title">
          <el-input v-model="editForm.title" placeholder="请输入书名" />
        </el-form-item>
        <el-form-item label="作者" prop="author">
          <el-input v-model="editForm.author" placeholder="请输入作者" />
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input
            v-model="editForm.description"
            type="textarea"
            :rows="3"
            placeholder="请输入书籍描述"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showEditDialog = false">取消</el-button>
          <el-button type="primary" @click="handleEdit">
            更新
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import api from '../../api'

export default {
  name: 'AdminBooks',
  data() {
    return {
      books: [],
      loading: false,
      searchKeyword: '',
      currentPage: 1,
      pageSize: 10,
      total: 0,
      showUploadDialog: false,
      showEditDialog: false,
      uploading: false,
      editingBook: null,
      uploadForm: {
        title: '',
        author: '',
        description: '',
        file: null
      },
      uploadRules: {
        title: [{ required: true, message: '请输入书名', trigger: 'blur' }],
        file: [{ required: true, message: '请选择文件', trigger: 'change' }]
      },
      editForm: {
        title: '',
        author: '',
        description: ''
      },
      editRules: {
        title: [{ required: true, message: '请输入书名', trigger: 'blur' }]
      }
    }
  },
  mounted() {
    this.fetchBooks()
  },
  methods: {
    async fetchBooks() {
      this.loading = true
      try {
        const params = {
          skip: (this.currentPage - 1) * this.pageSize,
          limit: this.pageSize
        }
        const data = await api.getBooks(params)
        this.books = data
        this.total = data.length
      } catch (error) {
        console.error('获取书籍列表失败:', error)
        this.$message.error('获取书籍列表失败')
      } finally {
        this.loading = false
      }
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN')
    },
    editBook(book) {
      this.editingBook = book
      this.editForm = {
        title: book.title,
        author: book.author || '',
        description: book.description || ''
      }
      this.showEditDialog = true
    },
    previewBook(bookId) {
      this.$router.push({ name: 'Reader', params: { bookId } })
    },
    async deleteBook(bookId) {
      try {
        await this.$confirm('确定删除此书籍吗？', '提示', {
          type: 'warning'
        })

        await api.deleteBook(bookId)
        this.$message.success('删除成功')
        this.fetchBooks()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error)
          this.$message.error('删除失败')
        }
      }
    },
    handleFileChange(file) {
      this.uploadForm.file = file.raw
    },
    async handleUpload() {
      this.$refs.uploadForm.validate(async (valid) => {
        if (!valid || !this.uploadForm.file) return

        this.uploading = true
        try {
          const formData = new FormData()
          formData.append('title', this.uploadForm.title)
          formData.append('author', this.uploadForm.author)
          formData.append('description', this.uploadForm.description)
          formData.append('file', this.uploadForm.file)

          await api.createBook(formData)
          this.$message.success('上传成功')
          this.showUploadDialog = false
          this.resetUploadForm()
          this.fetchBooks()
        } catch (error) {
          console.error('上传失败:', error)
          this.$message.error(error.response?.data?.detail || '上传失败')
        } finally {
          this.uploading = false
        }
      })
    },
    async handleEdit() {
      this.$refs.editForm.validate(async (valid) => {
        if (!valid) return

        try {
          await api.updateBook(this.editingBook.id, this.editForm)
          this.$message.success('更新成功')
          this.showEditDialog = false
          this.fetchBooks()
        } catch (error) {
          console.error('更新失败:', error)
          this.$message.error(error.response?.data?.detail || '更新失败')
        }
      })
    },
    resetUploadForm() {
      this.uploadForm = {
        title: '',
        author: '',
        description: '',
        file: null
      }
      this.$refs.uploadForm?.resetFields()
    },
    resetFilter() {
      this.searchKeyword = ''
      this.currentPage = 1
      this.fetchBooks()
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      this.fetchBooks()
    },
    handleCurrentChange(page) {
      this.currentPage = page
      this.fetchBooks()
    },
    goBack() {
      this.$router.push('/books')
    }
  }
}
</script>

<style scoped>
.admin-books-container {
  min-height: 100vh;
  background-color: #f5f5f5;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.header-left h1 {
  margin: 0;
  color: white;
  font-size: 20px;
}

.el-header {
  background-color: #409EFF;
  padding: 0 20px;
}

.filter-bar {
  margin-bottom: 20px;
  display: flex;
  gap: 10px;
  align-items: center;
}

.el-pagination {
  margin-top: 20px;
  justify-content: center;
}
</style>